import { Controller } from "@hotwired/stimulus"
import { sanitizeFilename, DEFAULT_FILENAME } from "../util/common"

// Connects to data-controller="download-file"
export default class extends Controller {
  static values = {
    shouldTrigger: {
      type: Boolean,
      default: false
    },
    method: {
      type: String,
      default: "GET"
    }
  }

  static EXTENDED_FILENAME = /;\s*filename\*\s*=\s*(?:[\w-]+'[^']*')?([^;]+)/i
  static QUOTED_FILENAME = /;\s*filename\s*=\s*"((?:[^"\\]|\\.)*)"/i
  static UNQUOTED_FILENAME = /;\s*filename\s*=\s*([^;]+)/i
  static QUOTED_PAIR = /\\(.)/g
  static CONTENT_TYPE_EXTENSION = /^[A-Za-z0-9]{1,10}$/

  download(event) {
    event.preventDefault()

    this.showDownloadOverlay(true)
    this.downloadFile(event)
      .catch(() => console.error("Download failed."))
      .finally(() => this.showDownloadOverlay(false))
  }

  showDownloadOverlay(show) {
    const overlay = document.getElementById("viewport-spinner-overlay")
    overlay.classList.toggle("hidden", !show)
  }

  async downloadFile(event) {
    const response = await this.fetchDownload(event.currentTarget)
    if (!response.ok) throw new Error("Network response was not ok")

    if (this.shouldTriggerValue) {
      const blob = await response.blob()
      const filename = this.extractFilename(response) || this.inferFilename(response) || DEFAULT_FILENAME
      this.triggerDownload(blob, filename)
    }
  }

  fetchDownload(target) {
    if (this.methodValue === "POST" && target.form) {
      return this.fetchViaPost(target.form)
    } else {
      return fetch(target.href, { credentials: "same-origin" })
    }
  }

  fetchViaPost(form) {
    const formData = new FormData(form)

    return fetch(form.action, {
      method: "POST",
      credentials: "same-origin",
      body: formData
    })
  }

  extractFilename(response) {
    const disposition = response.headers.get("Content-Disposition")
    if (!disposition) return null

    const filename = this.extendedFilename(disposition) || this.plainFilename(disposition)
    if (!filename) return null

    return sanitizeFilename(filename)
  }

  extendedFilename(disposition) {
    const match = disposition.match(this.constructor.EXTENDED_FILENAME)
    if (!match) return null

    return this.decodePercentEncoding(match[1].trim())
  }

  plainFilename(disposition) {
    const quoted = disposition.match(this.constructor.QUOTED_FILENAME)
    if (quoted) return this.unescapeQuotedPairs(quoted[1])

    const unquoted = disposition.match(this.constructor.UNQUOTED_FILENAME)

    return unquoted ? unquoted[1].trim() : null
  }

  decodePercentEncoding(value) {
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  unescapeQuotedPairs(value) {
    return value.replace(this.constructor.QUOTED_PAIR, "$1")
  }

  inferFilename(response) {
    const contentType = response.headers.get("Content-Type")
    if (!contentType) return null

    const extension = contentType.split("/")[1]?.split(";")[0]?.trim()
    if (!this.constructor.CONTENT_TYPE_EXTENSION.test(extension || "")) return null

    return `${DEFAULT_FILENAME}.${extension}`
  }

  triggerDownload(blob, filename) {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(link.href)
  }
}
